import { useState, useCallback } from 'react';

/**
 * Hook to track form field errors across multiple form fields
 * Useful for disabling submit buttons and preventing form submission
 */
export interface UseFormErrorsReturn {
  errors: Record<string, string | boolean>; // Map of field id -> error message or true
  hasErrors: boolean; // True if any field has an error
  setFieldError: (fieldId: string, error: string | boolean) => void;
  clearFieldError: (fieldId: string) => void;
  clearAllErrors: () => void;
  getFieldError: (fieldId: string) => string | boolean | undefined;
}

export function useFormErrors(initialFields: string[] = []): UseFormErrorsReturn {
  const [errors, setErrors] = useState<Record<string, string | boolean>>(() => {
    const initial: Record<string, string | boolean> = {};
    initialFields.forEach((fieldId) => {
      initial[fieldId] = false;
    });
    return initial;
  });

  const setFieldError = useCallback((fieldId: string, error: string | boolean) => {
    setErrors((prev) => ({
      ...prev,
      [fieldId]: error,
    }));
  }, []);

  const clearFieldError = useCallback((fieldId: string) => {
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[fieldId];
      return updated;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  const getFieldError = useCallback(
    (fieldId: string) => {
      return errors[fieldId];
    },
    [errors]
  );

  const hasErrors = Object.values(errors).some((error) => !!error);

  return {
    errors,
    hasErrors,
    setFieldError,
    clearFieldError,
    clearAllErrors,
    getFieldError,
  };
}
